"use client";

import * as React from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";

import { cn } from "@/lib/utils";
import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";

export interface SwitchProps extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
  label?: string;
  pointerEvents?: "none" | "auto";
  name?: string;
  id?: string;
  onLabel?: string;
  offLabel?: string;
}

const Switch = React.forwardRef<React.ComponentRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, label, pointerEvents, name, id, onLabel, offLabel, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    return (
      <motion.div
        className={cn(
          "relative flex flex-col items-start w-full min-w-20 max-w-20 gap-3",
          pointerEvents === "none" && "pointer-events-none",
        )}
        whileHover="hover"
        whileFocus="focus"
        whileTap="active"
        initial="rest"
        animate={isFocused ? "focus" : "rest"}
        exit="rest"
      >
        <AnimatePresence>
          {onLabel && offLabel && (
            <motion.span className="text-base text-muted-foreground  gap-0">
              {props.checked ? onLabel : offLabel}
            </motion.span>
          )}
        </AnimatePresence>
        <SwitchPrimitives.Root
          className={cn(
            "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-emerald-300 data-[state=unchecked]:bg-primary/30 ",
            className,
          )}
          {...props}
          ref={ref}
          onFocus={(e) => {
            handleFocus();
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            handleBlur();
            props.onBlur?.(e);
          }}
          onMouseEnter={(e) => {
            setIsHovered(true);
            props.onMouseEnter?.(e);
          }}
          onMouseLeave={(e) => {
            setIsHovered(false);
            props.onMouseLeave?.(e);
          }}
        >
          <SwitchPrimitives.Thumb
            className={cn(
              "pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0",
            )}
          />
        </SwitchPrimitives.Root>
      </motion.div>
    );
  },
);
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
